import {WonderlandEngine} from '../engine.js';
import {ImageLike} from '../types.js';
import {isString} from '../utils/object.js';
import {Texture} from '../wonderland.js';

import {ResourceManager} from './resource.js';

export function needsFlipY(image: ImageLike) {
    /* WebGL can't flip ImageBitmap on upload.
     * For consistency with WebGPU, disable ImageBitmap flipping entirely and let the user use:
     * https://developer.mozilla.org/en-US/docs/Web/API/Window/createImageBitmap#options */
    return image instanceof ImageBitmap ? 0 : 1;
}

/**
 * Constructor parameters object for {@link Texture} resources.
 *
 * ```js
 * const texture = engine.textures.create(image, {
 *     border: true
 * });
 * ```
 */
export interface TextureParameters {
    /**
     * If `true`, borders will be added to the texture to improve texture sampling quality.
     *
     * @note Textures with borders can't be partially updated.
     *
     * Defaults to `true` for images smaller than 1024, `false` otherwise.
     */
    border?: boolean;
}

/**
 * Manage textures.
 *
 * #### Creation
 *
 * Creating a texture is done using {@link TextureManager.load}:
 *
 * ```js
 * const texture = await engine.textures.load('my-image.png');
 * ```
 *
 * Alternatively, textures can be created directly via a loaded image using
 * {@link TextureManager.create}.
 *
 * @since 1.2.0
 */
export class TextureManager extends ResourceManager<Texture> {
    constructor(engine: WonderlandEngine) {
        super(engine, Texture);
    }

    static fetchImage(src: string | Blob, crossOrigin?: string): Promise<HTMLImageElement> {
        const url = src instanceof Blob ? URL.createObjectURL(src) : null;
        const promise: Promise<HTMLImageElement> = new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = crossOrigin ?? image.crossOrigin;
            image.addEventListener('load', () => resolve(image), {once: true});
            image.addEventListener('error', (e) => reject(e), {once: true});
            image.src = isString(src) ? src : url!;
        });
        if (url) {
            promise.finally(() => URL.revokeObjectURL(url));
        }
        return promise;
    }

    static fetchImageBitmap(
        src: string | Blob,
        options: Partial<ImageBitmapOptions>
    ): Promise<ImageBitmap> {
        if (src instanceof Blob) {
            return createImageBitmap(src, options);
        }
        return fetch(src)
            .then((r) => r.blob())
            .then((b) => createImageBitmap(b, options));
    }

    /**
     * Create a new texture from an image or video.
     *
     * #### Usage
     *
     * ```js
     * const img = new Image();
     * img.load = function(img) {
     *     const texture = engine.textures.create(img);
     * };
     * img.src = 'my-image.png';
     * ```
     *
     * @note The media must already be loaded. To automatically
     * load the media and create a texture, use {@link TextureManager.load} instead.
     *
     * @param image Media element to create the texture from.
     * @ret\urns The new texture with the media content.
     */
    create(image: ImageLike, params: TextureParameters = {}): Texture {
        const wasm = this.engine.wasm;

        if (image instanceof HTMLImageElement && !image.complete) {
            throw new Error('image must be ready to create a texture');
        }
        const jsImageIndex = wasm._images.length;
        wasm._images.push(image);
        wasm._imageSources.push(null);

        const width = (image as HTMLVideoElement).videoWidth ?? image.width;
        const height = (image as HTMLVideoElement).videoHeight ?? image.height;

        let border: boolean | undefined = params.border;
        if (border === undefined) {
            /* Backward compatibility: Do not add borders to large image
             * to allow calling `updateSubImage`. */
            const threshold = wasm._wl_renderer_atlas_downscaleThreshold();
            border = threshold === 0 || Math.max(width, height) < threshold;
        }

        const imageIndex = wasm._wl_image_create(jsImageIndex, border);
        wasm._wl_image_markReady(imageIndex, width, height, needsFlipY(image));

        /* Required because the image isn't a resource by itself, but will eventually be one. */
        const index = wasm._wl_texture_create(imageIndex);

        const instance = new Texture(this.engine, index);
        this._cache[instance.index] = instance;

        return instance;
    }

    /**
     * Load an image from URL as {@link Texture}.
     *
     * #### Usage
     *
     * ```js
     * const texture = await engine.textures.load('my-image.png');
     * ```
     *
     * @param filename URL to load from.
     * @param crossOrigin Cross origin flag for the image object.
     * @returns Loaded texture.
     */
    load(filename: string, crossOrigin?: string): Promise<Texture> {
        return TextureManager.fetchImage(filename, crossOrigin).then((image) =>
            this.create(image)
        );
    }
}
